import * as React from 'react';
import { CharInput } from './internal/components/CharInput';
import styles from './ConfirmationInput.module.css';
import { getNumbers } from './internal/utils/getNumbers';
import { normalizeValue } from './internal/utils/normalizeValue';
import { getNextIndex } from './internal/utils/getNextIndex';
import { parseFormat } from './internal/utils/parseFormat';
import { UIHeading1 } from '../../../typography';
import { FocusSource } from '../../../internal/types';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const ConfirmationInput = props => {
  const {
    format = '9999',
    disabled = false,
    invalid = false,
    autoFocus = false,
    dataMark,
    dataTestId,
    onChange,
    onComplete
  } = props;
  const [value, setValue] = React.useState('');
  const [focus, setFocus] = React.useState({
    index: -1,
    source: FocusSource.mouse
  });
  const inputsRef = React.useRef([]);
  const {
    groups,
    separators
  } = React.useMemo(() => parseFormat(format), [format]);
  const length = React.useMemo(() => groups.reduce((sum, group) => sum + group.length, 0), [groups]);
  const focusByIndex = React.useCallback(index => {
    var _inputsRef$current$in;
    (_inputsRef$current$in = inputsRef.current[index]) === null || _inputsRef$current$in === void 0 || _inputsRef$current$in.focus();
  }, [inputsRef]);
  const focusByValue = React.useCallback(value => {
    const index = getNextIndex(value, length);
    focusByIndex(index);
  }, [length, focusByIndex]);
  const handleFocus = index => source => {
    setFocus({
      index,
      source
    });
  };
  const handleBlur = () => {
    setFocus({
      index: -1,
      source: FocusSource.mouse
    });
  };
  const handleValueChange = React.useCallback(value => {
    setValue(value);
    if (onChange) {
      onChange(value);
    }
    const isCompleted = value.length >= length;
    if (isCompleted && onComplete) {
      onComplete(value);
      handleBlur();
    }
  }, [length, onChange, onComplete]);
  const handleChange = valueRaw => {
    const char = getNumbers(valueRaw);
    if (!char) {
      return;
    }
    const nextValue = value.concat(char);
    focusByValue(nextValue);
    handleValueChange(nextValue);
  };
  const handleClick = () => {
    focusByValue(value);
  };
  const handleKeyDown = event => {
    const {
      key
    } = event;
    const isBackspace = key === 'Backspace';
    if (isBackspace) {
      const lastIndex = value.length - 1;
      if (lastIndex < 0) {
        return;
      }
      const nextValue = value.slice(0, lastIndex);
      focusByIndex(lastIndex);
      handleValueChange(nextValue);
    }
  };
  const handlePaste = event => {
    event.preventDefault();
    const valueRaw = event.clipboardData.getData('text');
    const value = normalizeValue(valueRaw, length);
    focusByValue(value);
    handleValueChange(value);
  };
  React.useEffect(() => {
    setValue(normalizeValue(props.value, length));
  }, [length, props.value]);
  React.useEffect(() => {
    if (autoFocus) {
      focusByValue(value);
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        focusByValue(value);
      }
    };
    if (autoFocus) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoFocus, focusByIndex, focusByValue, value]);
  return /*#__PURE__*/_jsx("div", {
    className: styles['wrapper'],
    "data-mark": dataMark,
    "data-testid": dataTestId,
    children: groups.map((group, groupIndex) => /*#__PURE__*/_jsxs(React.Fragment, {
      children: [group.split('').map((_, charIndex) => {
        const index = groups.slice(0, groupIndex).reduce((sum, g) => sum + g.length, 0) + charIndex;
        return /*#__PURE__*/_jsx(CharInput, {
          ref: el => {
            inputsRef.current[index] = el;
          },
          value: value[index],
          disabled: disabled,
          invalid: invalid,
          focused: index === focus.index,
          focusSource: focus.source,
          onFocus: handleFocus(index),
          onBlur: handleBlur,
          onChange: handleChange,
          onClick: handleClick,
          onKeyDown: handleKeyDown,
          onPaste: handlePaste
        }, index);
      }), groupIndex < separators.length && /*#__PURE__*/_jsx("div", {
        "data-mark": "ConfirmationInput|CharSeparator",
        children: /*#__PURE__*/_jsx(UIHeading1, {
          children: separators[groupIndex]
        })
      })]
    }, groupIndex))
  });
};